import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Spin, message, Card, Space, Typography, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { fetchSqlData, fetchSqlDataWithQuery } from '../../../services/api';
import GenericFormFill, { FORM_PRINT_CSS } from './GenericFormFill';
import PrintButton from '../PrintButton';
import FileDownloadTool from '../FileDownloadTool';
import logger from '../../../utils/logger';
import { decodeSvgFile } from '../PdfFormCreationFiles/svgFileCodec';

// Utility: get form definition table, data table for a given route
function getFormInfoForPath(pathname: string): string[] {
  if (pathname.startsWith('/qaqc')) return ['qaqc_form_definitions', 'qaqc_form_records'];
  if (pathname.startsWith('/it')) return ['it_form_definitions', 'it_form_records'];
  if (pathname.startsWith('/to')) return ['to_form_definitions', 'to_form_records'];
  if (pathname.startsWith('/hse')) return ['hse_form_definitions', 'hse_form_records'];
  if (pathname.startsWith('/admin')) return ['adm_form_definitions', 'adm_form_records'];
  if (pathname.startsWith('/machinery')) return ['mach_form_definitions', 'mach_form_records'];
  if (pathname.startsWith('/material')) return ['mat_form_definitions', 'mat_form_records'];
  if (pathname.startsWith('/construction')) return ['const_form_definitions', 'const_form_records'];
  if (pathname.startsWith('/dcc')) return ['dcc_form_definitions', 'dcc_form_records'];
  if (pathname.startsWith('/official')) return ['off_form_definitions', 'off_form_records'];
  if (pathname.startsWith('/design')) return ['dsgn_form_definitions', 'dsgn_form_records'];
  return ['', ''];
}

const FormView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formDef, setFormDef] = useState<any>(null);
  const [formRecord, setFormRecord] = useState<any>(null);
  const [svgWithSize, setSvgWithSize] = useState<string>('');
  const printRootRef = useRef<HTMLDivElement>(null);

  // Fetch record and definition (same as FormEdit)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const t0 = performance.now();
        const [defTable, dataTable] = getFormInfoForPath(location.pathname);
        if (!defTable || !dataTable || !id) {
          message.error('Invalid form context.');
          setLoading(false);
          return;
        }
        const tRec0 = performance.now();
        const recArr = await fetchSqlData(dataTable, 'id', id);
        const tRec1 = performance.now();
        logger.debug(`[FormView] fetch record: ${(tRec1 - tRec0).toFixed(1)}ms`);
        if (!recArr || !recArr[0]) {
          message.error('Form record not found.');
          setLoading(false);
          return;
        }
        setFormRecord(recArr[0]);
        const query = `SELECT * FROM ${defTable} WHERE form_ref = '${recArr[0].form_ref}' AND form_description = '${recArr[0].form_description}'`;
        const tDef0 = performance.now();
        const defArr = await fetchSqlDataWithQuery(query);
        const tDef1 = performance.now();
        logger.debug(`[FormView] fetch definition: ${(tDef1 - tDef0).toFixed(1)}ms`);
        if (!defArr || !defArr[0]) {
          message.error('Form definition not found.');
          setLoading(false);
          return;
        }
        setFormDef(defArr[0]);
        const t1 = performance.now();
        logger.debug(`[FormView] total fetch time: ${(t1 - t0).toFixed(1)}ms`);
      } catch (err) {
        message.error('Failed to fetch form data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.pathname, id]);

  // Decode SVG and set explicit width/height like in FormFill
  useEffect(() => {
    if (!formDef) {
      setSvgWithSize('');
      return;
    }
    try {
      const t0 = performance.now();
      let svgString = '';
      if (formDef.svg_file) {
        svgString = decodeSvgFile(formDef.svg_file);
      }
      const tDecode = performance.now();
      const cleanedSvgString = svgString.replace(/<svg:/g, '<').replace(/<\/svg:/g, '</');
      // Parse form_json if backend returned it as a string
      let formJson: any = (formDef as any)?.form_json;
      if (typeof formJson === 'string') {
        try { formJson = JSON.parse(formJson); } catch { /* ignore */ }
      }
      let vbX = 0, vbY = 0, vbW = 1000, vbH = 1400;
      const match = cleanedSvgString.match(/viewBox="([^"]+)"/);
      if (match) {
        const parts = match[1].split(' ').map(Number);
        vbX = parts[0] || 0;
        vbY = parts[1] || 0;
        vbW = parts[2];
        vbH = parts[3];
      }
      const targetW = Number(formJson?.pdfWidth) || vbW;
      const targetH = Number(formJson?.pdfHeight) || vbH;
      const withSize = cleanedSvgString.replace(
        /<svg([^>]*)>/,
        `<svg$1 width="${targetW}" height="${targetH}" viewBox="${vbX} ${vbY} ${vbW} ${vbH}">`
      );
      setSvgWithSize(withSize);
      const t1 = performance.now();
      logger.debug(`[FormView] svg decode: ${(tDecode - t0).toFixed(1)}ms, set sized svg: ${(t1 - tDecode).toFixed(1)}ms`);
    } catch (e) {
      logger.warn('Failed to decode SVG for view');
      setSvgWithSize('');
    }
  }, [formDef]);

  // Prepare overlay fields and values
  const overlayFields = useMemo(() => {
    const raw = (formDef as any)?.form_json;
    let fj: any = raw;
    if (typeof fj === 'string') {
      try { fj = JSON.parse(fj); } catch { fj = null; }
    }
    return Array.isArray(fj?.fields) ? fj.fields : [];
  }, [formDef]);

  const fieldValues = useMemo(() => {
    if (!formRecord) return {} as Record<string, any>;
    let parsed: any = {};
    try {
      if (typeof formRecord.fields === 'string') parsed = JSON.parse(formRecord.fields);
      else if (typeof formRecord.fields === 'object') parsed = formRecord.fields;
    } catch {
      parsed = formRecord.fields;
    }
    return parsed || formRecord;
  }, [formRecord]);

  useEffect(() => {
    if (formRecord) {
      logger.debug(`Viewing form record id=${formRecord.id}, upload_path=${formRecord.upload_path}`);
    }
  }, [formRecord]);

  if (loading) return <Spin style={{ marginTop: 40 }} />;
  if (!formDef || !formRecord) return null;

  return (
    <Card style={{ margin: '0 auto', padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              const match = location.pathname.match(/^\/(\w+)/);
              const module = match ? match[1] : '';
              if (module) {
                navigate(`/${module}/forms/list`);
              } else {
                navigate(-1);
              }
            }}
          >
            Back
          </Button>
          <PrintButton
            targetRef={printRootRef}
            tooltip="Print form"
            cloneRoot
            extraCss={FORM_PRINT_CSS}
          />
          <Typography.Title level={4} style={{ margin: 0 }}>
            {formDef.form_description} — View
          </Typography.Title>
        </div>
        <div style={{ display: 'inline-block' }}>
          <GenericFormFill
            svg={svgWithSize}
            fields={overlayFields}
            formSpecificFields={{}}
            formDefinition={formDef}
            fieldValues={fieldValues}
            setFieldValues={() => { /* no-op to keep it read-only */ }}
            validationErrors={{}}
            loading={false}
            colorless={true}
            hideOverlaysInView={false}
            onSave={() => { /* no save in view */ }}
            showPrintButton={false}
            printRootRef={printRootRef}
          />
        </div>
        {formRecord?.upload_path && String(formRecord.upload_path).trim() !== '' && (
          <div className="no-print">
            <FileDownloadTool contentPath={formRecord.upload_path} buttonText="Attached Files" />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default FormView;
